/**
 * Contacts API client — thin wrapper around fetch for the contacts endpoints.
 */
import { apiClient, unwrapApiResponse } from '../shared/api-client';
import { checkedContactDetail, checkedContactList, checkedAddedNote, requireNoteSuccess, requireTrashedContact } from './contactDetailRecovery';
function parseRequestBody(body) {
    if (typeof body !== 'string') {
        return body;
    }
    try {
        return JSON.parse(body);
    }
    catch {
        return body;
    }
}
async function apiFetch(path, options = {}) {
    const method = (options.method || 'GET').toUpperCase();
    const body = parseRequestBody(options.body);
    switch (method) {
        case 'GET':
            return unwrapApiResponse(await apiClient.get(path));
        case 'POST':
            return unwrapApiResponse(await apiClient.post(path, body));
        case 'PUT':
            return unwrapApiResponse(await apiClient.put(path, body));
        case 'PATCH':
            return unwrapApiResponse(await apiClient.patch(path, body));
        case 'DELETE':
            return unwrapApiResponse(await apiClient.del(path));
        default:
            throw new Error(`Unsupported API method: ${method}`);
    }
}
export const contactsAPI = {
    // Contact CRUD
    async list(search, groupId, favoritesOnly) {
        const params = new URLSearchParams();
        if (search)
            params.set('search', search);
        if (groupId)
            params.set('groupId', groupId);
        if (favoritesOnly)
            params.set('favorites', 'true');
        const qs = params.toString();
        return checkedContactList(unwrapApiResponse(await apiClient.get(`/api/contacts${qs ? `?${qs}` : ''}`, { retryAuth: false })));
    },
    async get(contactId, signal) {
        const directory = contactId.startsWith('platform-');
        const target = directory ? contactId.slice(9) : contactId;
        if (!/^[0-9a-f]{8}-(?:[0-9a-f]{4}-){3}[0-9a-f]{12}$/i.test(target))
            throw new Error('This contact cannot be opened. Refresh Contacts.');
        const response = await apiClient.get(`/api/contacts/${directory ? 'platform/' : ''}${encodeURIComponent(target)}`, { retryAuth: false, ...(signal ? { signal } : {}) });
        return checkedContactDetail(unwrapApiResponse(response), contactId);
    },
    create(data) {
        return apiFetch('/api/contacts', {
            method: 'POST',
            body: JSON.stringify({
                name: data.name,
                email: data.email,
                phoneNumber: data.phone_number,
                contactHandle: data.contact_handle,
                organization: data.organization,
                notes: data.notes,
                avatarColor: data.avatar_color,
                isFavorite: data.is_favorite,
                linkedClientId: data.linked_client_id,
                groupIds: data.groupIds
            })
        });
    },
    update(contactId, data) {
        return apiFetch(`/api/contacts/${contactId}`, {
            method: 'PUT',
            body: JSON.stringify({
                name: data.name,
                email: data.email,
                phoneNumber: data.phone_number,
                contactHandle: data.contact_handle,
                organization: data.organization,
                notes: data.notes,
                avatarColor: data.avatar_color,
                isFavorite: data.is_favorite,
                linkedClientId: data.linked_client_id,
                groupIds: data.groupIds
            })
        });
    },
    async setFavorite(contactId, favorite) {
        if (!/^[0-9a-f]{8}-(?:[0-9a-f]{4}-){3}[0-9a-f]{12}$/i.test(contactId)) {
            throw new Error('Save this directory contact before changing favorites.');
        }
        const result = unwrapApiResponse(await apiClient.put(`/api/contacts/${encodeURIComponent(contactId)}`, { isFavorite: favorite }, { retryAuth: false }));
        if (!result || typeof result !== 'object' || !('success' in result) || result.success !== true) {
            throw new Error('Favorite change not confirmed. Refresh Contacts.');
        }
        return (await contactsAPI.get(contactId)).contact;
    },
    async delete(contactId) {
        const directory = contactId.startsWith('platform-');
        const target = directory ? contactId.slice(9) : contactId;
        if (!/^[0-9a-f]{8}-(?:[0-9a-f]{4}-){3}[0-9a-f]{12}$/i.test(target)) {
            throw new Error('Refresh Contacts before deleting this contact.');
        }
        const response = await apiClient.del(`/api/contacts/${directory ? 'platform/' : ''}${encodeURIComponent(target)}`, { retryAuth: false });
        if (response.status === 404) {
            // A lost success reply can make the deliberate retry return 404.
            // Do not equate a missing/foreign/merged contact with a confirmed deletion.
            const trash = unwrapApiResponse(await apiClient.get('/api/contacts?deleted=true', { retryAuth: false }));
            requireTrashedContact(trash, contactId);
        }
        else {
            const result = unwrapApiResponse(response);
            if (!result || typeof result !== 'object' || !('success' in result) || result.success !== true) {
                throw new Error('Deletion not confirmed. Refresh Contacts.');
            }
        }
        return { success: true };
    },
    // Handles and friend requests
    listHandles() {
        return apiFetch('/api/contacts/handles');
    },
    updatePrimaryHandle(data) {
        return apiFetch('/api/contacts/handles/primary', {
            method: 'PUT',
            body: JSON.stringify(data)
        });
    },
    lookup(query) {
        return apiFetch(`/api/contacts/lookup?query=${encodeURIComponent(query)}`);
    },
    listRequests() {
        return apiFetch('/api/contacts/requests');
    },
    sendRequest(data) {
        return apiFetch('/api/contacts/requests', {
            method: 'POST',
            body: JSON.stringify(data)
        });
    },
    respondToRequest(requestId, action) {
        return apiFetch(`/api/contacts/requests/${requestId}/respond`, {
            method: 'POST',
            body: JSON.stringify({ action })
        });
    },
    cancelRequest(requestId) {
        return apiFetch(`/api/contacts/requests/${requestId}/cancel`, {
            method: 'POST',
            body: JSON.stringify({})
        });
    },
    // Groups
    listGroups() {
        return apiFetch('/api/contacts/groups/list');
    },
    createGroup(data) {
        return apiFetch('/api/contacts/groups', {
            method: 'POST',
            body: JSON.stringify(data)
        });
    },
    updateGroup(groupId, data) {
        return apiFetch(`/api/contacts/groups/${groupId}`, {
            method: 'PUT',
            body: JSON.stringify(data)
        });
    },
    deleteGroup(groupId) {
        return apiFetch(`/api/contacts/groups/${groupId}`, { method: 'DELETE' });
    },
    setContactGroups(contactId, groupIds) {
        return apiFetch(`/api/contacts/${contactId}/groups`, {
            method: 'PUT',
            body: JSON.stringify({ groupIds })
        });
    },
    // Block list
    listBlocked() {
        return apiFetch('/api/contacts/blocked/list');
    },
    block(data) {
        return apiFetch('/api/contacts/blocked', {
            method: 'POST',
            body: JSON.stringify(data)
        });
    },
    unblock(blockId) {
        return apiFetch(`/api/contacts/blocked/${blockId}`, { method: 'DELETE' });
    },
    // Merge / dedup
    findDuplicates() {
        return apiFetch('/api/contacts/duplicates/list');
    },
    merge(primaryId, secondaryIds) {
        return apiFetch('/api/contacts/merge', {
            method: 'POST',
            body: JSON.stringify({ primaryId, secondaryIds })
        });
    },
    // Import
    import(contacts) {
        return apiFetch('/api/contacts/import', {
            method: 'POST',
            body: JSON.stringify({ contacts })
        });
    },
    // Migration
    migrateSpeedDial() {
        return apiFetch('/api/contacts/migrate-speed-dial', { method: 'POST' });
    },
    // Timeline
    getTimeline(contactId) {
        return apiFetch(`/api/contacts/${contactId}/timeline`);
    },
    // Notes CRUD
    getNotes(contactId) {
        return apiFetch(`/api/contacts/${contactId}/notes`);
    },
    async addNote(contactId, content) {
        const response = await apiClient.post(`/api/contacts/${encodeURIComponent(contactId)}/notes`, { content }, { retryAuth: false });
        return { note: checkedAddedNote(unwrapApiResponse(response), contactId) };
    },
    async updateNote(contactId, noteId, content) {
        const response = await apiClient.put(`/api/contacts/${encodeURIComponent(contactId)}/notes/${encodeURIComponent(noteId)}`, { content }, { retryAuth: false });
        requireNoteSuccess(unwrapApiResponse(response));
        return { success: true };
    },
    async deleteNote(contactId, noteId) {
        const response = await apiClient.del(`/api/contacts/${encodeURIComponent(contactId)}/notes/${encodeURIComponent(noteId)}`, { retryAuth: false });
        requireNoteSuccess(unwrapApiResponse(response));
        return { success: true };
    },
    // Sync
    sync(since) {
        const qs = since ? `?since=${encodeURIComponent(since)}` : '';
        return apiFetch(`/api/contacts/sync${qs}`);
    },
    // Google Contacts
    googleAuthUrl() {
        return apiFetch('/api/google-contacts/auth-url');
    },
    googleFetch() {
        return apiFetch('/api/google-contacts/fetch', { method: 'POST' });
    },
    googleImport(contacts) {
        return apiFetch('/api/google-contacts/import', {
            method: 'POST',
            body: JSON.stringify({ contacts })
        });
    }
};
